import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GitBranch, Sparkles } from "lucide-react";
import { toast } from "sonner";

const IMPORTANCE_OPTIONS = [
  { code: "nice_to_have", label: "Nice to have" },
  { code: "important", label: "Important" },
  { code: "very_important", label: "Very important" },
  { code: "deal_breaker", label: "Deal-breaker" },
];

const IMPORTANCE_LABELS = Object.fromEntries(
  IMPORTANCE_OPTIONS.map((item) => [item.code, item.label])
);
IMPORTANCE_LABELS.unknown = "Not set";

const DECISION_LABELS = {
  accept: "Worth it",
  maybe: "Not sure yet",
  reject: "Too much",
};

const RIPPLE_LABELS = {
  commitment_matters_more: "Firmer commitment",
  unresolved_matters_more: "More important to resolve",
  concern_matters_more: "Stronger reason to review",
  commitment_matters_less: "Carries less weight",
  unresolved_matters_less: "Less of a blocker",
  concern_matters_less: "Weaker reason to review",
  unchanged: "No change",
};

function rippleCopy(item) {
  const university = item.university_name || "This university";
  const city = item.city_name || "this city";

  if (item.ripple === "commitment_matters_more") {
    return (
      "You already decided " +
      city +
      "'s higher cost was worth it. If City mattered more, that commitment would carry more weight in why " +
      university +
      " still fits."
    );
  }
  if (item.ripple === "unresolved_matters_more") {
    return (
      "You are still unsure about " +
      city +
      "'s higher cost. If City mattered more, resolving that uncertainty would become more important before you felt confident about " +
      university +
      "."
    );
  }
  if (item.ripple === "concern_matters_more") {
    return (
      "You said " +
      city +
      "'s higher cost was too much. If City mattered more, that would become a stronger reason to review " +
      university +
      "."
    );
  }
  if (item.ripple === "commitment_matters_less") {
    return (
      "You already decided " +
      city +
      "'s higher cost was worth it. If City mattered less, that trade-off would carry less weight in your view of " +
      university +
      "."
    );
  }
  if (item.ripple === "unresolved_matters_less") {
    return (
      "You are still unsure about " +
      city +
      "'s higher cost. If City mattered less, that unresolved trade-off would become less of a blocker for " +
      university +
      "."
    );
  }
  if (item.ripple === "concern_matters_less") {
    return (
      "You said " +
      city +
      "'s higher cost was too much. That decision would still be part of your history, but it would carry less weight when reviewing " +
      university +
      "."
    );
  }
  return "Your current city-cost decision would keep the same weight.";
}

export default function RealisationRippleSimulator({
  realisationId,
  currentImportance,
  disabled = false,
  onChanged,
}) {
  const [open, setOpen] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setOpen(false);
    setSimulating(false);
    setSaving(false);
    setPreview(null);
    setError("");
  }, [realisationId, currentImportance]);

  const simulate = async (importance) => {
    if (simulating || saving || disabled) return;
    setSimulating(true);
    setPreview(null);
    setError("");
    try {
      const { data } = await api.post(
        "/realisations/" + realisationId + "/ripple/simulate",
        { importance }
      );
      setPreview(data);
    } catch {
      setError(
        "We couldn't preview those ripple effects just now. Refresh and try again."
      );
    } finally {
      setSimulating(false);
    }
  };

  const confirm = async () => {
    if (!preview?.would_change || saving || simulating || disabled) return;
    setSaving(true);
    setError("");
    try {
      await api.patch("/realisations/" + realisationId, {
        importance: preview.simulated_importance,
      });
      toast.success(
        "City importance updated — your previous version is still in How you got here."
      );
      setPreview(null);
      setOpen(false);
      await onChanged?.();
    } catch {
      setError(
        "We couldn't make that your new importance just now. Refresh and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const directionCopy =
    preview?.direction === "more"
      ? "If City mattered more to you, your existing city-cost decisions would carry more weight."
      : preview?.direction === "less"
        ? "If City mattered less to you, those same decisions would carry less weight."
        : "This is already how important City is to you.";

  return (
    <section
      className="mt-5 rounded-md border border-indigo-200 bg-indigo-50/40 p-4 sm:p-5"
      data-testid={"realisation-ripple-" + realisationId}
    >
      <div className="flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-[#4C1D95]" />
        <div className="text-[10px] font-underground-sc tracking-[0.14em] text-[#4C1D95]">
          Ripple effects
        </div>
      </div>

      <div className="mt-2 text-sm font-semibold text-stone-900">
        What if the city mattered differently to you?
      </div>
      <p className="mt-1 text-xs leading-relaxed text-stone-600">
        Preview how changing this Realisation would change the weight of your
        existing city-cost decisions across your shortlist.
      </p>

      {!open ? (
        <>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setOpen(true);
              setPreview(null);
              setError("");
            }}
            data-testid={"realisation-ripple-open-" + realisationId}
            className="mt-3 text-xs font-semibold text-[#4C1D95] hover:underline disabled:cursor-not-allowed disabled:text-stone-400 disabled:no-underline"
          >
            Explore the ripple
          </button>
          {disabled && (
            <div className="mt-2 text-[11px] text-stone-500">
              Save or undo your unsaved Realisation edits before exploring
              ripple effects.
            </div>
          )}
        </>
      ) : (
        <div
          className="mt-3 rounded-md border border-stone-200 bg-white p-3.5"
          data-testid={"realisation-ripple-panel-" + realisationId}
        >
          <div className="text-xs font-semibold text-stone-800">
            How important should City be in this preview?
          </div>
          <div className="mt-1 text-[11px] leading-relaxed text-stone-500">
            Current: {IMPORTANCE_LABELS[currentImportance] || currentImportance}.
            Nothing is saved while you explore.
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {IMPORTANCE_OPTIONS.map((choice) => {
              const current = choice.code === currentImportance;
              const selected =
                preview?.simulated_importance === choice.code;
              return (
                <button
                  key={choice.code}
                  type="button"
                  disabled={simulating || saving || disabled || current}
                  onClick={() => simulate(choice.code)}
                  data-testid={
                    "realisation-ripple-choice-" +
                    choice.code +
                    "-" +
                    realisationId
                  }
                  className={
                    "min-h-10 rounded-md border px-3 py-2 text-xs font-semibold disabled:cursor-default " +
                    (selected
                      ? "border-[#4C1D95] bg-violet-50 text-[#4C1D95]"
                      : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]") +
                    (current
                      ? " disabled:bg-stone-100 disabled:text-stone-400"
                      : " disabled:opacity-60")
                  }
                >
                  {choice.label}
                  {current ? " · Current" : ""}
                </button>
              );
            })}
            <button
              type="button"
              disabled={simulating || saving}
              onClick={() => {
                setOpen(false);
                setPreview(null);
                setError("");
              }}
              className="min-h-10 px-2 py-2 text-xs text-stone-500 hover:underline disabled:opacity-50"
            >
              Cancel
            </button>
          </div>

          {simulating && (
            <div className="mt-3 text-[11px] text-stone-500">
              Working out the ripple across your choices…
            </div>
          )}

          {preview && (
            <div
              className="mt-4 rounded-md border border-violet-200 bg-violet-50/30 p-3.5"
              data-testid={"realisation-ripple-preview-" + realisationId}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-violet-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-[#4C1D95]">
                  Preview only
                </span>
                <span className="text-xs font-semibold text-stone-700">
                  {IMPORTANCE_LABELS[preview.simulated_importance] ||
                    preview.simulated_importance}
                </span>
              </div>

              <div className="mt-2 text-sm font-semibold text-stone-900">
                {directionCopy}
              </div>
              <div className="mt-1 text-[11px] leading-relaxed text-stone-500">
                Nothing has changed. WUA has not removed, reordered, re-ranked
                or reweighted any university.
              </div>

              {preview.effects?.length ? (
                <div className="mt-3 grid gap-2.5">
                  {preview.effects.map((item, index) => (
                    <div
                      key={[
                        item.university_name || "",
                        item.city_name || "",
                        index,
                      ].join("|")}
                      className="min-w-0 break-words [overflow-wrap:anywhere] rounded-md border border-stone-200 bg-white px-3 py-3"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-stone-900">
                          {item.university_name || "University"}
                        </span>
                        {item.city_name && (
                          <span className="text-[11px] text-stone-500">
                            {item.city_name}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-semibold">
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-stone-600">
                          {DECISION_LABELS[item.response] || item.response}
                        </span>
                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[#4C1D95]">
                          {RIPPLE_LABELS[item.ripple] || item.ripple}
                        </span>
                      </div>
                      <p className="mt-2 text-xs leading-relaxed text-stone-600">
                        {rippleCopy(item)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 rounded-md border border-dashed border-stone-300 bg-white px-3 py-3 text-xs leading-relaxed text-stone-500">
                  You do not yet have a current city-cost decision to ripple
                  across your shortlist. You can still change how important
                  City is, but there is no option-level consequence to preview
                  yet.
                </div>
              )}

              {preview.would_change && (
                <button
                  type="button"
                  disabled={saving || disabled}
                  onClick={confirm}
                  data-testid={
                    "realisation-ripple-confirm-" + realisationId
                  }
                  className="mt-3 inline-flex min-h-10 items-center gap-1.5 rounded-md bg-[#4C1D95] px-3 py-2 text-xs font-semibold text-white hover:bg-[#3B0764] disabled:opacity-60"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {saving
                    ? "Saving your new importance…"
                    : "Make this how much City matters"}
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 text-[11px] text-rose-700">{error}</div>
          )}
        </div>
      )}
    </section>
  );
}
